<template>
  <div class="gf-editor" :style="{ '--chrome-offset': `${chromeOffset}px` }">
    <div ref="chrome" class="gf-chrome">
    <header class="gf-top">
      <div class="gf-top-title">
        <router-link :to="{ name: 'QuestionnaireList' }" class="gf-back" title="回到問卷管理">
          <i class="ri-arrow-left-line"></i>
        </router-link>
        <input v-model="name" class="gf-doc-name" placeholder="表單名稱" />
        <span class="gf-status">{{ statusLabel }}</span>
      </div>
      <div class="gf-top-actions">
        <button type="button" class="gf-text-btn" @click.stop="openPreview">預覽</button>
        <button type="button" class="gf-text-btn" @click="save({ notify: true })">儲存</button>
        <button v-if="status !== 'published'" type="button" class="gf-publish" @click="status === 'ended' ? republish() : publish()">
          {{ status === 'ended' ? '重新發布' : '發布' }}
        </button>
        <div v-if="id" class="gf-more">
          <button type="button" class="gf-more-btn" title="更多" aria-label="更多" @click.stop="toggleMore">
            <i class="ri-more-2-fill"></i>
          </button>
          <div v-if="moreOpen" class="gf-more-list" @click.stop>
            <button type="button" @click="runMore(copyQuestionnaire)">建立副本</button>
            <button type="button" @click="runMore(copyFillLink)">複製連結</button>
            <button type="button" @click="runMore(openStats)">統計</button>
            <button type="button" @click="runMore(clearResponses)">清除回覆</button>
            <button type="button" @click="runMore(removeQuestionnaire)">刪除問卷</button>
          </div>
        </div>
      </div>
    </header>

    <nav class="gf-tabs">
      <button type="button" :class="{ on: tab === 'questions' }" @click="tab = 'questions'">問題</button>
      <button type="button" :class="{ on: tab === 'settings' }" @click="tab = 'settings'">設定</button>
      <router-link v-if="id" class="gf-tab-link" :to="{ name: 'QuestionnaireResponses', params: { id } }">
        回覆{{ responseCount ? ` ${responseCount}` : '' }}
      </router-link>
    </nav>
    </div>

    <div v-if="ready && tab === 'questions'" class="gf-canvas">
      <p v-if="error" class="gf-error">{{ error }}</p>
      <div class="gf-stack">
        <template v-for="(page, pageIndex) in document.pages" :key="page.id">
          <div class="gf-row">
            <article
              class="gf-card section-card"
              :class="{
                'gf-header': pageIndex === 0,
                active: isSectionActive(page, pageIndex),
                'drag-over': dragOverKey === 'page:' + page.id
              }"
              @click="activeId = sectionKey(page)"
              @dragover.prevent="onBlockDragOver('page:' + page.id)"
              @drop="onBlockDrop(pageIndex, 0)"
            >
              <div class="section-head">
                <p>第 {{ pageIndex + 1 }} 個區段，共 {{ document.pages.length }} 個</p>
              </div>
              <template v-if="pageIndex === 0">
                <input v-model="name" class="gf-form-title" placeholder="表單名稱" />
                <textarea v-model="description" class="gf-form-desc" rows="1" placeholder="問卷說明"></textarea>
              </template>
              <template v-else>
                <input v-model="page.title" class="section-title" placeholder="區段標題" />
                <textarea v-model="page.description" class="gf-form-desc" rows="1" placeholder="說明"></textarea>
              </template>
              <footer class="q-foot">
                <div class="section-menu">
                  <button type="button" title="區段功能" aria-label="區段功能" @click.stop="toggleSectionMenu(page.id, $event)">
                    <i class="ri-more-2-fill"></i>
                  </button>
                  <div v-if="sectionMenuId === page.id" class="section-menu-list" :class="{ up: sectionMenuUp }" @click.stop>
                    <button type="button" :disabled="pageIndex === 0" @click="runSectionAction(() => movePage(pageIndex, -1))">上移區段</button>
                    <button type="button" :disabled="pageIndex === document.pages.length - 1" @click="runSectionAction(() => movePage(pageIndex, 1))">下移區段</button>
                    <button type="button" @click="runSectionAction(() => duplicatePage(pageIndex))">複製區段</button>
                    <button type="button" :disabled="pageIndex === 0" @click="runSectionAction(() => mergePage(pageIndex))">與上一個區段合併</button>
                    <button type="button" :disabled="document.pages.length === 1" @click="runSectionAction(() => removePage(pageIndex))">刪除區段</button>
                  </div>
                </div>
              </footer>
            </article>
            <aside v-if="isSectionActive(page, pageIndex)" class="gf-tools">
              <button type="button" title="新增問題" @click="insertAfter('question')"><i class="ri-add-line"></i></button>
              <button type="button" title="新增標題與說明" @click="insertAfter('text')"><i class="ri-text"></i></button>
              <button type="button" title="新增圖片" @click="insertAfter('image')"><i class="ri-image-add-line"></i></button>
              <button type="button" title="新增區段" @click="insertAfter('page')"><i class="ri-split-cells-horizontal"></i></button>
            </aside>
          </div>

          <div
            v-for="(block, blockIndex) in page.blocks"
            :key="block.id"
            class="gf-row"
            :class="{ dragging: dragFrom === block.id, 'drag-over': dragOverKey === block.id }"
            @dragover.prevent="onBlockDragOver(block.id)"
            @drop="onBlockDrop(pageIndex, blockIndex)"
          >
            <article class="gf-card" :class="{ active: activeId === block.id }" @click="activeId = block.id">
              <span
                class="drag-handle"
                draggable="true"
                role="button"
                aria-label="拖曳排序"
                title="拖曳排序"
                @click.stop
                @dragstart.stop="onBlockDragStart(pageIndex, blockIndex, block.id, $event)"
                @dragend="onBlockDragEnd"
              >
                <i class="ri-draggable"></i>
              </span>
              <template v-if="block.type === 'question' && activeId === block.id">
                <div class="q-head">
                  <input v-model="block.question.prompt" class="q-prompt" placeholder="問題" />
                  <select v-model="block.question.questionType" class="q-type" @change="ensureOptions(block.question)">
                    <option value="single">選擇題</option>
                    <option value="multi">核取方塊</option>
                    <option value="scale">線性刻度</option>
                    <option value="short_text">簡答</option>
                    <option value="long_text">詳答</option>
                  </select>
                </div>

                <textarea
                  v-if="block.question.showDescription"
                  v-model="block.question.helpText"
                  class="q-help"
                  rows="2"
                  placeholder="說明"
                ></textarea>

                <input
                  v-if="isText(block.question)"
                  v-model="block.question.placeholder"
                  class="answer-preview"
                  :placeholder="block.question.questionType === 'long_text' ? '詳答文字' : '簡答文字'"
                />

                <div v-if="block.question.questionType === 'scale'" class="presets">
                  <button type="button" @click="applyScalePreset(block.question, ['1','2','3','4','5'])">1 到 5</button>
                  <button type="button" @click="applyScalePreset(block.question, ['很不同意','不同意','普通','同意','很同意'])">不同意到很同意</button>
                  <button type="button" @click="applyScalePreset(block.question, ['很不喜歡','不喜歡','普通','喜歡','很喜歡'])">很不喜歡到很喜歡</button>
                </div>

                <div v-if="!isText(block.question)" class="choices">
                  <div v-for="(option, optionIndex) in block.question.options" :key="option.id" class="choice-wrap">
                    <div class="choice">
                      <span class="mark" :class="block.question.questionType === 'multi' ? 'box' : 'dot'"></span>
                      <input v-model="option.label" class="choice-input" :placeholder="option.isOther ? '其他' : '選項 ' + (optionIndex + 1)" />
                      <input
                        v-if="option.isOther && option.otherTextEnabled"
                        class="other-preview"
                        type="text"
                        disabled
                        placeholder="請補充"
                      />
                      <button type="button" class="ghost" @click.stop="toggleTags(option.id)">
                        {{ tagButtonLabel(option) }}
                      </button>
                      <button type="button" class="icon-btn" title="上移" :disabled="option.isOther || optionIndex === 0" @click="moveOption(block.question, optionIndex, -1)">
                        <i class="ri-arrow-up-s-line"></i>
                      </button>
                      <button type="button" class="icon-btn" title="下移" :disabled="option.isOther || optionIndex >= block.question.options.length - 1 || block.question.options[optionIndex + 1].isOther" @click="moveOption(block.question, optionIndex, 1)">
                        <i class="ri-arrow-down-s-line"></i>
                      </button>
                      <button type="button" class="icon-btn" title="刪除選項" @click="block.question.options.splice(optionIndex, 1)">
                        <i class="ri-close-line"></i>
                      </button>
                      <select
                        v-if="block.question.branchByAnswer && block.question.questionType === 'single'"
                        v-model="option.goToPageId"
                        class="go-to"
                      >
                        <option value="">下一區段</option>
                        <option v-for="target in laterPages(pageIndex)" :key="target.id" :value="target.id">
                          第 {{ target.index + 1 }} 區段{{ target.title ? `：${target.title}` : '' }}
                        </option>
                        <option value="submit">送出問卷</option>
                      </select>
                    </div>
                    <div v-if="option.isOther" class="other-flags">
                      <label><input v-model="option.otherTextEnabled" type="checkbox" /> 可填文字</label>
                      <label v-if="option.otherTextEnabled"><input v-model="option.otherTextRequired" type="checkbox" /> 文字必填</label>
                    </div>
                    <div v-if="openTagOptionId === option.id" class="tag-panel" @click.stop>
                      <TagSelectorMultiple
                        v-model="option.tagIds"
                        label="勾選這個選項時貼上"
                        hint="可不選。只會貼互動標籤。"
                        tag-source="manual"
                      />
                    </div>
                  </div>
                  <div class="choice add-line">
                    <span class="mark" :class="block.question.questionType === 'multi' ? 'box' : 'dot'"></span>
                    <button type="button" class="add-option" @click="addOption(block.question)">新增選項</button>
                    <template v-if="canAddOther(block.question)">
                      <span class="or">或</span>
                      <button type="button" class="other-link" @click="addOther(block.question)">新增「其他」</button>
                    </template>
                  </div>
                </div>

                <div
                  v-if="canShuffle(block.question) || block.question.questionType === 'multi' || (canBranch(pageIndex) && block.question.questionType === 'single')"
                  class="q-extra"
                >
                  <label v-if="canShuffle(block.question)">
                    <input v-model="block.question.shuffleOptions" type="checkbox" />
                    隨機排序選項
                  </label>
                  <label v-if="block.question.questionType === 'multi'" class="max-select">
                    最多勾選
                    <input
                      class="max-input"
                      type="number"
                      min="1"
                      step="1"
                      placeholder="不限"
                      :value="block.question.maxSelections || ''"
                      @input="setMaxSelections(block.question, $event.target.value)"
                    />
                    項
                  </label>
                  <label v-if="canBranch(pageIndex) && block.question.questionType === 'single'">
                    <input v-model="block.question.branchByAnswer" type="checkbox" />
                    依答案前往區段
                  </label>
                </div>
                <p v-if="block.question.branchByAnswer && block.question.questionType === 'single'" class="branch-hint">
                  離開這個區段時，依這題的答案決定下一區段。同一區段有多題時，以最後一題為準。
                </p>

                <footer class="q-foot">
                  <div class="q-foot-icons">
                    <button type="button" title="上移" :disabled="blockIndex === 0" @click="moveBlock(page, blockIndex, -1)"><i class="ri-arrow-up-line"></i></button>
                    <button type="button" title="下移" :disabled="blockIndex === page.blocks.length - 1" @click="moveBlock(page, blockIndex, 1)"><i class="ri-arrow-down-line"></i></button>
                    <button type="button" title="複製問題" @click="duplicateBlock(page, block)"><i class="ri-file-copy-line"></i></button>
                    <button type="button" title="刪除問題" @click="removeBlock(page, blockIndex)"><i class="ri-delete-bin-6-line"></i></button>
                    <select v-if="document.pages.length > 1" class="move-page" @change="moveBlockToPage(pageIndex, blockIndex, Number($event.target.value)); $event.target.value = ''">
                      <option value="">移到區段</option>
                      <option v-for="(target, targetIndex) in document.pages" :key="target.id" :value="targetIndex" :disabled="targetIndex === pageIndex">第 {{ targetIndex + 1 }} 區段</option>
                    </select>
                  </div>
                  <label class="show-desc">
                    <input v-model="block.question.showDescription" type="checkbox" />
                    顯示說明
                  </label>
                  <label class="required-switch">
                    必填
                    <input v-model="block.question.required" type="checkbox" />
                    <i></i>
                  </label>
                </footer>
              </template>

              <template v-else-if="block.type === 'question'">
                <p class="preview-prompt">{{ block.question.prompt || '未命名的問題' }}</p>
                <p v-if="block.question.showDescription && block.question.helpText" class="preview-help">{{ block.question.helpText }}</p>
                <p v-if="block.question.questionType === 'multi' && block.question.maxSelections" class="preview-help">
                  最多勾選 {{ block.question.maxSelections }} 項
                </p>
                <p v-if="isText(block.question)" class="preview-line">{{ block.question.questionType === 'long_text' ? '詳答文字' : '簡答文字' }}</p>
                <div v-else class="choices preview">
                  <div v-for="option in block.question.options.slice(0, 3)" :key="option.id" class="choice">
                    <span class="mark" :class="block.question.questionType === 'multi' ? 'box' : 'dot'"></span>
                    <span>{{ option.label || '選項' }}</span>
                    <span v-if="option.isOther && option.otherTextEnabled" class="other-preview">請補充</span>
                  </div>
                </div>
              </template>

              <template v-else-if="block.type === 'text'">
                <template v-if="activeId === block.id">
                  <input v-model="block.title" class="section-title" placeholder="標題" />
                  <textarea v-if="block.showDescription" v-model="block.text" class="text-block" rows="2" placeholder="說明"></textarea>
                </template>
                <template v-else>
                  <p class="preview-prompt">{{ block.title || '標題' }}</p>
                  <p v-if="block.showDescription && block.text" class="preview-help">{{ block.text }}</p>
                </template>
                <footer v-if="activeId === block.id" class="q-foot">
                  <div class="q-foot-icons">
                    <button type="button" title="複製" @click="duplicateBlock(page, block)"><i class="ri-file-copy-line"></i></button>
                    <button type="button" title="刪除" @click="removeBlock(page, blockIndex)"><i class="ri-delete-bin-6-line"></i></button>
                    <select v-if="document.pages.length > 1" class="move-page" @change="moveBlockToPage(pageIndex, blockIndex, Number($event.target.value)); $event.target.value = ''">
                      <option value="">移到區段</option>
                      <option v-for="(target, targetIndex) in document.pages" :key="target.id" :value="targetIndex" :disabled="targetIndex === pageIndex">第 {{ targetIndex + 1 }} 區段</option>
                    </select>
                  </div>
                  <label class="show-desc">
                    <input v-model="block.showDescription" type="checkbox" />
                    顯示說明
                  </label>
                </footer>
              </template>

              <template v-else-if="block.type === 'image'">
                <ImageUploader v-if="activeId === block.id" v-model="block.imageUrl" :upload-fn="uploadImage" />
                <img v-else-if="block.imageUrl" :src="block.imageUrl" alt="" class="preview-image" />
                <p v-else class="preview-prompt">圖片</p>
                <footer v-if="activeId === block.id" class="q-foot">
                  <div class="q-foot-icons">
                    <button type="button" title="刪除" @click="removeBlock(page, blockIndex)"><i class="ri-delete-bin-6-line"></i></button>
                  </div>
                </footer>
              </template>

              <template v-else-if="block.type === 'intake'">
                <p class="section-kicker">網址帶入欄位</p>
                <select v-model="block.fieldKey" class="q-type intake-select">
                  <option value="">選擇欄位</option>
                  <option v-for="field in document.intakeFields" :key="field.fieldKey" :value="field.fieldKey">{{ field.label || field.fieldKey }}</option>
                </select>
                <footer v-if="activeId === block.id" class="q-foot">
                  <div class="q-foot-icons">
                    <button type="button" title="刪除" @click="removeBlock(page, blockIndex)"><i class="ri-delete-bin-6-line"></i></button>
                  </div>
                </footer>
              </template>
            </article>
            <aside v-if="activeId === block.id" class="gf-tools">
              <button type="button" title="新增問題" @click="insertAfter('question')"><i class="ri-add-line"></i></button>
              <button type="button" title="新增標題與說明" @click="insertAfter('text')"><i class="ri-text"></i></button>
              <button type="button" title="新增圖片" @click="insertAfter('image')"><i class="ri-image-add-line"></i></button>
              <button type="button" title="新增區段" @click="insertAfter('page')"><i class="ri-split-cells-horizontal"></i></button>
            </aside>
          </div>
        </template>
      </div>
    </div>

    <div v-else-if="ready" class="gf-canvas">
      <p v-if="error" class="gf-error">{{ error }}</p>
      <div class="gf-stack">
        <article class="gf-card settings-card">
          <h2>收卷</h2>
          <p class="hint">沒有填開始時間，一發布就開始收。有填就照那個時間。</p>
          <label>開始收卷<input v-model="startsAt" type="datetime-local" /></label>
          <label>結束時間<input v-model="endsAt" type="datetime-local" /></label>
          <label>收卷上限<input v-model.number="responseLimit" type="number" min="1" placeholder="不限" /></label>
        </article>

        <article class="gf-card settings-card">
          <h2>重複填寫</h2>
          <div class="setting-item">
            <div class="setting-switch">
              <span class="setting-name">
                <label for="q-allow-repeat">允許同一人重複填寫</label>
                <button type="button" class="help-btn" aria-label="查看允許同一人重複填寫的說明" :aria-expanded="helpKey === 'allowRepeat'" @click.stop="toggleHelp('allowRepeat')">?</button>
              </span>
              <span class="switch">
                <input id="q-allow-repeat" v-model="allowRepeat" type="checkbox" role="switch" />
                <i></i>
              </span>
            </div>
            <p v-if="helpKey === 'allowRepeat'" class="help-pop" @click.stop>關閉時，對得到人的填寫者送出一次後，再開只能看到已填寫完成。</p>
          </div>
          <div class="setting-item">
            <div class="setting-switch" :class="{ off: !allowRepeat }">
              <span class="setting-name">
                <label for="q-overwrite">重複填寫時覆蓋之前的回覆</label>
                <button type="button" class="help-btn" aria-label="查看覆蓋之前回覆的說明" :aria-expanded="helpKey === 'overwrite'" @click.stop="toggleHelp('overwrite')">?</button>
              </span>
              <span class="switch">
                <input id="q-overwrite" v-model="overwritePrevious" type="checkbox" role="switch" :disabled="!allowRepeat" />
                <i></i>
              </span>
            </div>
            <p v-if="helpKey === 'overwrite'" class="help-pop" @click.stop>關閉「允許同一人重複填寫」時，這個開關會一起關閉。開啟後，同一人再送出會改掉他最近一筆回覆。關閉則每送出一次就多留一筆。</p>
          </div>
          <div class="setting-item">
            <div class="setting-switch">
              <span class="setting-name">
                <label for="q-show-previous">再次開啟時顯示之前填寫的內容</label>
                <button type="button" class="help-btn" aria-label="查看顯示之前填寫內容的說明" :aria-expanded="helpKey === 'showPrevious'" @click.stop="toggleHelp('showPrevious')">?</button>
              </span>
              <span class="switch">
                <input id="q-show-previous" v-model="showPreviousAnswers" type="checkbox" role="switch" />
                <i></i>
              </span>
            </div>
            <p v-if="helpKey === 'showPrevious'" class="help-pop" @click.stop>開啟後，同一人再次打開會看到上次的作答。允許重複填寫時，題目會帶入上次的答案。</p>
          </div>
        </article>

        <article class="gf-card settings-card">
          <h2>完成頁</h2>
          <label>完成訊息<textarea v-model="completionMessage" rows="3" placeholder="謝謝你的填寫"></textarea></label>
          <div v-for="(button, index) in completionButtons" :key="index" class="button-row">
            <input v-model="button.label" placeholder="按鈕文字" :class="{ invalid: buttonChecked && !String(button.label || '').trim() }" />
            <select v-model="button.actionType">
              <option value="url">開啟網址</option>
              <option value="tel">撥打電話</option>
              <option value="close">關閉頁面</option>
            </select>
            <input v-if="button.actionType === 'url'" v-model="button.uri" placeholder="https://" :class="{ invalid: buttonChecked && !String(button.uri || '').trim() }" />
            <label v-if="button.actionType === 'url'" class="check">
              <input v-model="button.openInNewWindow" type="checkbox" />
              開啟新視窗
            </label>
            <input v-if="button.actionType === 'tel'" v-model="button.phone" placeholder="電話" :class="{ invalid: buttonChecked && !String(button.phone || '').trim() }" />
            <button type="button" class="icon-btn" :disabled="index === 0" @click="moveButton(index, -1)"><i class="ri-arrow-up-s-line"></i></button>
            <button type="button" class="icon-btn" :disabled="index === completionButtons.length - 1" @click="moveButton(index, 1)"><i class="ri-arrow-down-s-line"></i></button>
            <button type="button" class="icon-btn" @click="completionButtons.splice(index, 1)"><i class="ri-close-line"></i></button>
          </div>
          <button type="button" class="other-link" @click="completionButtons.push({ label: '', actionType: 'url', uri: '', text: '', phone: '', openInNewWindow: false })">新增按鈕</button>
        </article>

        <article class="gf-card settings-card">
          <h2>網址帶入欄位</h2>
          <p class="hint">填寫網址可以帶入這些參數。其中一欄可以標成手機。</p>
          <div v-for="(field, index) in document.intakeFields" :key="index" class="button-row">
            <input v-model="field.fieldKey" placeholder="參數代碼" />
            <input v-model="field.label" placeholder="顯示名稱" />
            <label class="check"><input v-model="field.isPhone" type="checkbox" @change="markPhone(index)" /> 手機</label>
            <label class="check"><input v-model="field.visible" type="checkbox" /> 顯示</label>
            <button type="button" class="ghost" @click="insertIntake(field.fieldKey)">放到問卷</button>
            <button type="button" class="icon-btn" @click="document.intakeFields.splice(index, 1)"><i class="ri-close-line"></i></button>
          </div>
          <button type="button" class="other-link" @click="addIntake">新增欄位</button>
          <p v-if="intakeFieldKeys.length && !fillPath" class="hint">儲存問卷後，這裡會出現帶好參數的完整連結。</p>
        </article>

        <article v-if="fillPath" class="gf-card settings-card">
          <h2>填寫連結</h2>
          <p class="fill-path">{{ fillPath }}</p>
          <button type="button" class="other-link" @click="copyFillLink">複製連結</button>
          <template v-if="intakeFillPath">
            <h2>帶入參數連結</h2>
            <p class="hint">已把參數代碼接在填寫連結後面。複製後把等號後面換成實際的值。</p>
            <p class="fill-path">{{ intakeFillPath }}</p>
            <button type="button" class="other-link" @click="copyIntakeFillLink">複製帶入連結</button>
          </template>
        </article>
      </div>
    </div>

    <QuestionnairePreview
      :open="previewOpen"
      :name="name"
      :description="description"
      :pages="document.pages"
      :intake-fields="document.intakeFields"
      :completion-message="completionMessage"
      :completion-buttons="completionButtons"
      @close="previewOpen = false"
    />
  </div>
</template>

<script>
import ImageUploader from '@/components/ImageUploader.vue';
import QuestionnairePreview from './Preview.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import apiClient from '@/api/axios.js';
import { questionnaireService } from '@/api/modules/questionnaires.js';
import { showSuccess, showError, showConfirmDeferred, fireSwalDeferred } from '@/utils/swal.js';

function uid() {
  return crypto.randomUUID();
}

function blankQuestion(type = 'single') {
  return {
    id: uid(),
    prompt: '',
    helpText: '',
    questionType: type,
    required: true,
    placeholder: '',
    showDescription: false,
    shuffleOptions: false,
    branchByAnswer: false,
    maxSelections: null,
    options: [blankOption()]
  };
}

function blankOption() {
  return {
    id: uid(),
    label: '',
    isOther: false,
    otherTextEnabled: false,
    otherTextRequired: false,
    tagIds: [],
    goToPageId: ''
  };
}

export default {
  name: 'QuestionnaireForm',
  components: { ImageUploader, TagSelectorMultiple, QuestionnairePreview },
  data() {
    const first = { id: uid(), type: 'question', question: blankQuestion() };
    return {
      ready: false,
      error: '',
      tab: 'questions',
      activeId: first.id,
      id: this.$route.params.id || '',
      name: '表單名稱',
      description: '',
      status: 'draft',
      responseCount: 0,
      answeredQuestionIds: [],
      fillPath: '',
      completionMessage: '謝謝您撥冗完成問卷填寫',
      completionButtons: [],
      startsAt: '',
      endsAt: '',
      responseLimit: null,
      allowRepeat: false,
      overwritePrevious: false,
      showPreviousAnswers: true,
      openTagOptionId: '',
      dragFrom: '',
      dragOverKey: '',
      dragBlock: null,
      sectionMenuId: '',
      sectionMenuUp: false,
      moreOpen: false,
      helpKey: '',
      buttonChecked: false,
      previewOpen: false,
      chromeOffset: 105,
      document: { pages: [{ id: uid(), title: '', description: '', blocks: [first] }], intakeFields: [] }
    };
  },
  computed: {
    statusLabel() {
      if (this.status === 'published') return '收卷中';
      if (this.status === 'ended') return '已結束';
      return '草稿';
    },
    intakeFieldKeys() {
      const keys = [];
      const seen = new Set();
      (this.document.intakeFields || []).forEach((field) => {
        const key = String(field.fieldKey || '').trim();
        if (!key || key === 'flow' || seen.has(key)) return;
        seen.add(key);
        keys.push(key);
      });
      return keys;
    },
    intakeFillPath() {
      if (!this.fillPath || !this.intakeFieldKeys.length) return '';
      try {
        const url = new URL(this.fillPath);
        this.intakeFieldKeys.forEach((key) => {
          if (!url.searchParams.has(key)) url.searchParams.append(key, '');
        });
        return url.toString();
      } catch (error) {
        const joiner = this.fillPath.includes('?') ? '&' : '?';
        const query = this.intakeFieldKeys.map((key) => `${encodeURIComponent(key)}=`).join('&');
        return `${this.fillPath}${joiner}${query}`;
      }
    }
  },
  watch: {
    allowRepeat(value) {
      if (!value) this.overwritePrevious = false;
    }
  },
  async mounted() {
    document.addEventListener('click', this.closeMenus);
    window.addEventListener('resize', this.syncChromeOffset);
    if (this.id) await this.load();
    this.ready = true;
    this.$nextTick(() => this.syncChromeOffset());
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeMenus);
    window.removeEventListener('resize', this.syncChromeOffset);
  },
  methods: {
    syncChromeOffset() {
      const height = this.$refs.chrome?.offsetHeight;
      if (height) this.chromeOffset = height;
    },
    isText(question) {
      return question.questionType === 'short_text' || question.questionType === 'long_text';
    },
    canShuffle(question) {
      return question.questionType === 'single' || question.questionType === 'multi';
    },
    canBranch(pageIndex) {
      return pageIndex < this.document.pages.length - 1;
    },
    setMaxSelections(question, value) {
      const text = String(value || '').trim();
      const number = Number(text);
      question.maxSelections = text && Number.isInteger(number) && number >= 1 ? number : null;
    },
    laterPages(pageIndex) {
      return this.document.pages.slice(pageIndex + 1).map((page, offset) => ({
        id: page.id,
        title: page.title,
        index: pageIndex + 1 + offset
      }));
    },
    canAddOther(question) {
      return (question.questionType === 'single' || question.questionType === 'multi')
        && !(question.options || []).some((option) => option.isOther);
    },
    toLocal(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    async load() {
      const response = await questionnaireService.get(this.id);
      const row = response.data;
      this.name = row.name;
      this.description = row.description || '';
      this.status = row.status;
      this.responseCount = row.responseCount || 0;
      this.answeredQuestionIds = row.answeredQuestionIds || [];
      this.fillPath = row.fillPath || '';
      this.completionMessage = row.completionMessage || '';
      this.completionButtons = (row.completionButtons || [])
        .filter((button) => ['url', 'tel', 'close'].includes(button.actionType || 'url'))
        .map((button) => ({
          ...button,
          openInNewWindow: button.openInNewWindow === true
        }));
      this.startsAt = this.toLocal(row.startsAt);
      this.endsAt = this.toLocal(row.endsAt);
      this.responseLimit = row.responseLimit;
      this.allowRepeat = row.allowRepeat === true;
      this.overwritePrevious = this.allowRepeat && row.overwritePrevious === true;
      this.showPreviousAnswers = row.showPreviousAnswers !== false;
      this.document = row.document;
      this.normalizeTagIds();
      this.activeId = this.document.pages?.[0]?.blocks?.[0]?.id || 'header';
    },
    normalizeTagIds() {
      (this.document.pages || []).forEach((page) => {
        (page.blocks || []).forEach((block) => {
          (block.question?.options || []).forEach((option) => {
            if (!Array.isArray(option.tagIds)) option.tagIds = [];
            if (option.goToPageId == null) option.goToPageId = '';
          });
          if (block.question) {
            if (block.question.helpText == null) block.question.helpText = '';
            if (block.question.showDescription == null) {
              block.question.showDescription = Boolean(String(block.question.helpText).trim());
            }
            if (block.question.shuffleOptions == null) block.question.shuffleOptions = false;
            if (block.question.branchByAnswer == null) block.question.branchByAnswer = false;
            this.pinOther(block.question);
          }
        });
      });
    },
    toggleTags(optionId) {
      this.openTagOptionId = this.openTagOptionId === optionId ? '' : optionId;
    },
    tagButtonLabel(option) {
      const count = (option.tagIds || []).length;
      if (this.openTagOptionId === option.id) return count ? `收合貼標（${count}）` : '收合貼標';
      return count ? `已選 ${count} 個標籤` : '貼標';
    },
    sectionKey(page) {
      return `page:${page.id}`;
    },
    isSectionActive(page, pageIndex) {
      return this.activeId === this.sectionKey(page) || (pageIndex === 0 && this.activeId === 'header');
    },
    sectionIndex(activeId) {
      if (activeId === 'header') return 0;
      if (typeof activeId === 'string' && activeId.startsWith('page:')) {
        const index = this.document.pages.findIndex((page) => page.id === activeId.slice(5));
        return index === -1 ? null : index;
      }
      return null;
    },
    locate(blockId) {
      for (let pageIndex = 0; pageIndex < this.document.pages.length; pageIndex += 1) {
        const blockIndex = this.document.pages[pageIndex].blocks.findIndex((block) => block.id === blockId);
        if (blockIndex >= 0) return { pageIndex, blockIndex };
      }
      return null;
    },
    makeBlock(kind) {
      if (kind === 'text') return { id: uid(), type: 'text', title: '', text: '', showDescription: false };
      if (kind === 'image') return { id: uid(), type: 'image', imageUrl: '' };
      if (kind === 'intake') return { id: uid(), type: 'intake', fieldKey: '' };
      return { id: uid(), type: 'question', question: blankQuestion() };
    },
    insertAfter(kind) {
      if (kind === 'page') {
        const loc = this.locate(this.activeId);
        const newPage = { id: uid(), title: '', description: '', blocks: [] };
        if (loc) {
          newPage.blocks = this.document.pages[loc.pageIndex].blocks.splice(loc.blockIndex + 1);
          this.document.pages.splice(loc.pageIndex + 1, 0, newPage);
        } else {
          const after = this.sectionIndex(this.activeId);
          const at = after == null ? this.document.pages.length : after + 1;
          this.document.pages.splice(at, 0, newPage);
        }
        this.activeId = newPage.blocks[0]?.id || this.sectionKey(newPage);
        return;
      }
      const block = this.makeBlock(kind);
      const loc = this.locate(this.activeId);
      if (loc) {
        this.document.pages[loc.pageIndex].blocks.splice(loc.blockIndex + 1, 0, block);
      } else {
        const index = this.sectionIndex(this.activeId);
        const page = this.document.pages[index == null ? 0 : index];
        page.blocks.unshift(block);
      }
      this.activeId = block.id;
    },
    insertIntake(fieldKey) {
      const block = this.makeBlock('intake');
      block.fieldKey = fieldKey || '';
      const loc = this.locate(this.activeId);
      const page = loc ? this.document.pages[loc.pageIndex] : this.document.pages[this.document.pages.length - 1];
      page.blocks.push(block);
      this.activeId = block.id;
      this.tab = 'questions';
    },
    duplicateBlock(page, block) {
      const copy = JSON.parse(JSON.stringify(block));
      copy.id = uid();
      if (copy.question) {
        copy.question.id = uid();
        (copy.question.options || []).forEach((option) => { option.id = uid(); });
      }
      const index = page.blocks.findIndex((item) => item.id === block.id);
      page.blocks.splice(index + 1, 0, copy);
      this.activeId = copy.id;
    },
    payload() {
      return {
        name: this.name,
        description: this.description,
        completionMessage: this.completionMessage,
        completionButtons: this.completionButtons,
        startsAt: this.startsAt ? new Date(this.startsAt).toISOString() : null,
        endsAt: this.endsAt ? new Date(this.endsAt).toISOString() : null,
        responseLimit: this.responseLimit || null,
        allowRepeat: this.allowRepeat,
        overwritePrevious: this.allowRepeat && this.overwritePrevious,
        showPreviousAnswers: this.showPreviousAnswers,
        document: this.document
      };
    },
    openPreview() {
      this.previewOpen = true;
    },
    completionButtonIssues() {
      const issues = [];
      this.completionButtons.forEach((button, index) => {
        const nth = `完成頁第 ${index + 1} 個按鈕`;
        if (!String(button.label || '').trim()) issues.push(`${nth}請填寫按鈕文字`);
        if (button.actionType === 'url' && !String(button.uri || '').trim()) issues.push(`${nth}請填寫網址`);
        if (button.actionType === 'tel' && !String(button.phone || '').trim()) issues.push(`${nth}請填寫電話`);
      });
      return issues;
    },
    async save({ notify = false } = {}) {
      this.error = '';
      const issues = this.completionButtonIssues();
      this.buttonChecked = issues.length > 0;
      if (issues.length) {
        this.tab = 'settings';
        this.error = issues[0];
        return;
      }
      const creating = !this.id;
      try {
        if (this.id) {
          await questionnaireService.update(this.id, this.payload());
        } else {
          const response = await questionnaireService.create(this.payload());
          this.id = response.data.id;
        }
        if (notify) await showSuccess('儲存完畢');
        if (creating && this.id) {
          this.$router.replace({ name: 'EditQuestionnaire', params: { id: this.id } });
        }
      } catch (error) {
        this.error = error?.response?.data?.message || error?.message || '儲存失敗';
      }
    },
    async publish() {
      await this.save();
      if (!this.id || this.error) return;
      try {
        await questionnaireService.publish(this.id);
        this.status = 'published';
      } catch (error) {
        this.error = error?.response?.data?.message || '無法發布';
      }
    },
    async republish() {
      await this.save();
      if (this.error) return;
      await questionnaireService.republish(this.id);
      this.status = 'published';
    },
    moveButton(index, delta) {
      const next = index + delta;
      const [button] = this.completionButtons.splice(index, 1);
      this.completionButtons.splice(next, 0, button);
    },
    onBlockDragStart(pageIndex, blockIndex, blockId, event) {
      this.dragFrom = blockId;
      this.dragBlock = { pageIndex, blockIndex };
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', blockId);
    },
    onBlockDragOver(key) {
      if (!this.dragBlock) return;
      this.dragOverKey = key;
    },
    onBlockDrop(pageIndex, blockIndex) {
      const from = this.dragBlock;
      this.onBlockDragEnd();
      if (!from || (from.pageIndex === pageIndex && from.blockIndex === blockIndex)) return;
      const [block] = this.document.pages[from.pageIndex].blocks.splice(from.blockIndex, 1);
      let insertAt = blockIndex;
      if (from.pageIndex === pageIndex && from.blockIndex < blockIndex) insertAt -= 1;
      const target = this.document.pages[pageIndex].blocks;
      target.splice(Math.max(0, Math.min(insertAt, target.length)), 0, block);
    },
    onBlockDragEnd() {
      this.dragFrom = '';
      this.dragOverKey = '';
      this.dragBlock = null;
    },
    moveBlock(page, index, delta) {
      const next = index + delta;
      const [block] = page.blocks.splice(index, 1);
      page.blocks.splice(next, 0, block);
    },
    moveBlockToPage(fromPageIndex, blockIndex, toPageIndex) {
      if (toPageIndex === fromPageIndex || Number.isNaN(toPageIndex)) return;
      const [block] = this.document.pages[fromPageIndex].blocks.splice(blockIndex, 1);
      this.document.pages[toPageIndex].blocks.push(block);
    },
    removeBlock(page, index) {
      const block = page.blocks[index];
      const answered = block.type === 'question' && this.answeredQuestionIds.includes(block.question.id);
      if (answered && !window.confirm('這題已有回答，刪除後統計會改列在已刪除題目，已貼的標籤不會收回。確定刪除？')) {
        return;
      }
      page.blocks.splice(index, 1);
      if (this.activeId === block.id) {
        const next = page.blocks[index] || page.blocks[index - 1];
        this.activeId = next ? next.id : this.sectionKey(page);
      }
    },
    toggleSectionMenu(pageId, event) {
      if (this.sectionMenuId === pageId) {
        this.sectionMenuId = '';
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      this.sectionMenuUp = rect.bottom + 220 > window.innerHeight;
      this.sectionMenuId = pageId;
    },
    closeSectionMenu() {
      this.sectionMenuId = '';
    },
    closeMenus() {
      this.sectionMenuId = '';
      this.moreOpen = false;
      this.helpKey = '';
    },
    toggleHelp(key) {
      this.helpKey = this.helpKey === key ? '' : key;
    },
    toggleMore() {
      this.sectionMenuId = '';
      this.moreOpen = !this.moreOpen;
    },
    runMore(action) {
      this.moreOpen = false;
      action.call(this);
    },
    runSectionAction(action) {
      this.closeSectionMenu();
      action();
    },
    duplicatePage(pageIndex) {
      const source = this.document.pages[pageIndex];
      const copy = JSON.parse(JSON.stringify(source));
      copy.id = uid();
      copy.blocks = (copy.blocks || []).map((block) => {
        const next = { ...block, id: uid() };
        if (next.question) {
          next.question = {
            ...next.question,
            id: uid(),
            options: (next.question.options || []).map((option) => ({ ...option, id: uid() }))
          };
        }
        return next;
      });
      this.document.pages.splice(pageIndex + 1, 0, copy);
      this.activeId = copy.blocks[0]?.id || this.sectionKey(copy);
    },
    movePage(pageIndex, delta) {
      const next = pageIndex + delta;
      if (next < 0 || next >= this.document.pages.length) return;
      const [page] = this.document.pages.splice(pageIndex, 1);
      this.document.pages.splice(next, 0, page);
    },
    mergePage(pageIndex) {
      if (pageIndex < 1) return;
      const page = this.document.pages[pageIndex];
      this.document.pages[pageIndex - 1].blocks.push(...page.blocks);
      this.document.pages.splice(pageIndex, 1);
    },
    removePage(pageIndex) {
      if (this.document.pages.length < 2) return;
      if (!window.confirm('確定刪除這一區段？區段裡的題目會一起刪除。')) return;
      this.document.pages.splice(pageIndex, 1);
    },
    pinOther(question) {
      const options = question.options || [];
      const others = options.filter((option) => option.isOther);
      if (!others.length) return;
      const rest = options.filter((option) => !option.isOther);
      question.options = [...rest, ...others];
    },
    moveOption(question, index, delta) {
      const option = question.options[index];
      const next = index + delta;
      if (!option || option.isOther || next < 0 || next >= question.options.length) return;
      if (question.options[next].isOther) return;
      const [moved] = question.options.splice(index, 1);
      question.options.splice(next, 0, moved);
    },
    openStats() {
      this.$router.push({ name: 'QuestionnaireStats', params: { id: this.id } });
    },
    async copyFillLink() {
      if (!this.fillPath) {
        await showError('尚無填寫連結');
        return;
      }
      if (await this.writeClipboard(this.fillPath)) {
        await showSuccess('已複製連結');
        return;
      }
      await showError('無法複製連結');
    },
    async copyIntakeFillLink() {
      if (!this.intakeFillPath) return;
      if (await this.writeClipboard(this.intakeFillPath)) {
        await showSuccess('已複製帶入連結');
        return;
      }
      await showError('無法複製連結');
    },
    async writeClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (error) {
        const input = document.createElement('textarea');
        input.value = text;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        let copied = false;
        try {
          copied = document.execCommand('copy');
        } catch (copyError) {
          copied = false;
        }
        input.remove();
        return copied;
      }
    },
    async clearResponses() {
      const confirmed = await showConfirmDeferred(
        '清除回覆',
        '這份問卷已收下的回覆與作答會全部刪除，且無法復原。問卷、題目與已經貼上的標籤會保留。清掉之後，同一人可以再填一次。',
        '清除',
        '取消'
      );
      if (!confirmed) return;
      try {
        const response = await questionnaireService.clearResponses(this.id);
        const removed = response?.data?.removed || 0;
        this.responseCount = 0;
        await showSuccess(removed ? `已清除 ${removed} 份回覆` : '沒有回覆可清除');
      } catch (error) {
        this.error = error?.response?.data?.message || '無法清除回覆';
      }
    },
    async removeQuestionnaire() {
      const confirmed = await showConfirmDeferred(
        '刪除問卷',
        '問卷會從列表移出，回覆仍保留。可在狀態選「已刪除」查看回覆與統計。填寫連結會失效。',
        '刪除',
        '取消'
      );
      if (!confirmed) return;
      try {
        await questionnaireService.remove(this.id);
        this.$router.push({ name: 'QuestionnaireList' });
      } catch (error) {
        this.error = error?.response?.data?.message || '無法刪除';
      }
    },
    async copyQuestionnaire() {
      const name = await this.askCopyName(this.name);
      if (!name) return;
      const response = await questionnaireService.copy(this.id, { name });
      if (response?.data?.id) this.$router.push({ name: 'EditQuestionnaire', params: { id: response.data.id } });
    },
    askCopyName(currentName) {
      return fireSwalDeferred({
        title: '建立副本',
        input: 'text',
        inputLabel: '問卷名稱',
        inputValue: `${currentName || '表單名稱'} 的副本`,
        showCancelButton: true,
        confirmButtonText: '確定',
        cancelButtonText: '取消',
        confirmButtonColor: '#5746e3',
        cancelButtonColor: '#6c757d',
        reverseButtons: true,
        inputValidator: (value) => (String(value || '').trim() ? undefined : '請填寫問卷名稱')
      }).then((result) => (result.isConfirmed ? String(result.value || '').trim() : ''));
    },
    addOption(question) {
      const option = blankOption();
      const otherIndex = question.options.findIndex((item) => item.isOther);
      if (otherIndex === -1) question.options.push(option);
      else question.options.splice(otherIndex, 0, option);
    },
    addOther(question) {
      if (!this.canAddOther(question)) return;
      question.options.push({ ...blankOption(), label: '其他', isOther: true, otherTextEnabled: true });
      this.pinOther(question);
    },
    ensureOptions(question) {
      if (this.isText(question)) {
        question.options = [];
        return;
      }
      if (!question.options || question.options.length < 1) {
        question.options = [blankOption()];
      }
    },
    applyScalePreset(question, labels) {
      question.options = labels.map((label) => ({ ...blankOption(), label }));
    },
    addIntake() {
      this.document.intakeFields.push({ fieldKey: '', label: '', isPhone: false, visible: false });
    },
    markPhone(index) {
      this.document.intakeFields.forEach((field, fieldIndex) => {
        field.isPhone = fieldIndex === index && this.document.intakeFields[index].isPhone;
      });
    },
    async uploadImage(file) {
      const formData = new FormData();
      formData.append('file', file);
      return apiClient.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
  }
};
</script>

<style scoped>
.gf-editor {
  margin: -8px;
  padding-top: var(--chrome-offset, 105px);
  min-height: calc(100vh - 72px);
  background: #f4f2ff;
  color: #202124;
}
.gf-chrome {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 90;
  background: #fff;
}
.sidebar.collapsed ~ .main-content .gf-chrome {
  left: 64px;
}
@media (max-width: 768px) {
  .gf-chrome,
  .sidebar.collapsed ~ .main-content .gf-chrome {
    left: 0;
  }
}
.gf-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #e6e8f0;
}
.gf-top-title, .gf-top-actions { display: flex; align-items: center; gap: 8px; min-width: 0; }
.gf-back { color: #5f6368; font-size: 20px; text-decoration: none; }
.gf-doc-name {
  border: 0;
  font-size: 18px;
  width: min(360px, 40vw);
  padding: 6px 8px;
  border-radius: 4px;
  background: transparent;
}
.gf-doc-name:focus { outline: none; background: #f4f2ff; }
.gf-status { color: #5f6368; font-size: 13px; white-space: nowrap; }
.gf-text-btn, .gf-publish {
  border: 0;
  background: transparent;
  color: #3c4043;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  text-decoration: none;
  font-size: 14px;
}
.gf-text-btn:hover { background: #f4f2ff; }
.gf-publish { background: #5746e3; color: #fff; padding: 8px 24px; border-radius: 4px; }
.gf-publish:hover { background: #4636c9; }
.gf-more { position: relative; }
.gf-more-btn {
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #3c4043;
  cursor: pointer;
  font-size: 20px;
}
.gf-more-btn:hover, .gf-more-btn[aria-expanded='true'] { background: #f4f2ff; }
.gf-more-list {
  position: absolute;
  right: 0;
  top: 44px;
  z-index: 20;
  min-width: 160px;
  padding: 8px 0;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.gf-more-list button {
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: 0;
  background: transparent;
  color: #202124;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.gf-more-list button:hover { background: #f1f3f4; }
.gf-tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  background: #fff;
  border-bottom: 1px solid #e6e8f0;
}
.gf-tabs button, .gf-tab-link {
  border: 0;
  background: transparent;
  padding: 12px 16px;
  color: #5f6368;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  text-decoration: none;
  font-size: 14px;
}
.gf-tabs button.on { color: #5746e3; border-bottom-color: #5746e3; }
.gf-tab-link:hover { color: #202124; }
.gf-canvas { padding: 24px 72px 80px; }
.q-help, .preview-help { width: 100%; margin: 8px 0 0; color: #5f6368; font-size: 14px; }
.q-help { border: 0; border-bottom: 1px solid #e0e0e0; background: transparent; resize: vertical; padding: 8px 0; }
.q-extra { display: flex; flex-wrap: wrap; gap: 12px 16px; align-items: center; margin-top: 12px; color: #5f6368; font-size: 13px; }
.q-extra label { display: flex; align-items: center; gap: 6px; }
.max-input { width: 72px; border: 1px solid #dadce0; border-radius: 4px; padding: 4px 8px; background: #fff; }
.branch-hint { margin: 8px 0 0; color: #70757a; font-size: 12px; }
.go-to { max-width: 180px; font-size: 13px; }
.gf-row { position: relative; }
.gf-stack { width: min(820px, 100%); margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
.gf-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  padding: 22px 24px;
}
.drag-handle {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  color: #9aa0a6;
  cursor: grab;
  padding: 2px 10px;
  line-height: 1;
  font-size: 18px;
}
.gf-row.dragging { opacity: 0.45; }
.gf-row.drag-over .gf-card,
.section-card.drag-over { box-shadow: inset 0 3px 0 #5746e3; }
.gf-card.active { box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px rgba(0, 0, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.12); }
.gf-card.active:not(.gf-header) { border-left: 6px solid #5746e3; }
.gf-header { border-top: 10px solid #5746e3; border-top-left-radius: 0; padding-top: 16px; }
.gf-header .section-head { top: -10px; transform: translateY(-100%); }
.gf-form-title, .section-title, .q-prompt, .choice-input, .text-block, .gf-form-desc, .answer-preview {
  width: 100%;
  border: 0;
  background: transparent;
  color: #202124;
  font: inherit;
}
.gf-form-title { font-size: 32px; padding: 8px 0; }
.gf-form-desc, .answer-preview, .text-block { border-bottom: 1px solid #e0e0e0; padding: 8px 0; color: #202124; }
.answer-preview, .text-block { resize: vertical; }
.gf-form-desc {
  resize: none;
  overflow: hidden;
  field-sizing: content;
  line-height: 22px;
  min-height: 38px;
}
.gf-form-desc::placeholder, .q-prompt::placeholder, .choice-input::placeholder { color: #70757a; }
.q-prompt:focus, .choice-input:focus, .gf-form-title:focus, .gf-form-desc:focus, .text-block:focus, .answer-preview:focus, .section-title:focus {
  outline: none;
  border-bottom: 2px solid #5746e3;
  background: #f8f9fa;
}
.q-head { display: flex; gap: 16px; align-items: flex-start; }
.q-prompt { flex: 1; font-size: 16px; padding: 12px; background: #f8f9fa; border-bottom: 2px solid transparent; }
.q-type, .settings-card input, .settings-card textarea, .settings-card select, .button-row input, .button-row select, .move-page {
  border: 1px solid #dadce0;
  border-radius: 4px;
  padding: 8px 10px;
  background: #fff;
  color: #202124;
}
.q-type { min-width: 148px; }
.choices { margin-top: 8px; }
.choice { display: flex; align-items: center; gap: 8px; min-height: 40px; flex-wrap: wrap; }
.choice-input { width: auto; flex: 1 1 140px; min-width: 96px; }
.other-preview {
  width: auto;
  flex: 1 1 160px;
  min-width: 120px;
  border: 0;
  border-bottom: 1px solid #dadce0;
  background: transparent;
  color: #9aa0a6;
  padding: 6px 0;
}
.mark { width: 18px; height: 18px; border: 2px solid #bdbdbd; flex: 0 0 auto; }
.mark.dot { border-radius: 50%; }
.mark.box { border-radius: 2px; }
.add-option, .other-link, .ghost, .icon-btn, .q-foot-icons button, .presets button {
  border: 0;
  background: transparent;
  color: #70757a;
  cursor: pointer;
}
.other-link { color: #5746e3; }
.or { color: #70757a; margin: 0 4px; }
.ghost { font-size: 13px; white-space: nowrap; }
.icon-btn, .q-foot-icons button { width: 32px; height: 32px; border-radius: 50%; color: #5f6368; }
.icon-btn:hover, .q-foot-icons button:hover, .gf-tools button:hover { background: #f1f3f4; }
.icon-btn:disabled, .q-foot-icons button:disabled { opacity: 0.35; cursor: default; }
.other-flags { display: flex; gap: 12px; margin: 0 0 8px 26px; color: #5f6368; font-size: 13px; }
.tag-panel { margin: 4px 0 12px 26px; padding: 12px; background: #f8f9fa; border-radius: 8px; }
.presets { display: flex; flex-wrap: wrap; gap: 8px; margin: 12px 0; }
.presets button { border: 1px solid #dadce0; border-radius: 16px; padding: 4px 12px; }
.q-foot {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 8px;
  border-top: 1px solid #e0e0e0;
}
.q-foot-icons { display: flex; align-items: center; gap: 4px; }
.show-desc { display: flex; align-items: center; gap: 6px; color: #5f6368; font-size: 13px; }
.required-switch { position: relative; display: flex; align-items: center; gap: 8px; color: #5f6368; }
.required-switch input { position: absolute; opacity: 0; }
.required-switch i {
  width: 36px;
  height: 14px;
  background: #bdbdbd;
  border-radius: 8px;
  position: relative;
}
.required-switch i::after {
  content: '';
  position: absolute;
  top: -3px;
  left: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fafafa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
.required-switch input:checked + i { background: #c5bff6; }
.required-switch input:checked + i::after { left: 16px; background: #5746e3; }
.preview-prompt { margin: 0 0 12px; font-size: 16px; }
.preview-line { margin: 0; color: #9aa0a6; border-bottom: 1px dotted #dadce0; padding-bottom: 8px; }
.preview-image { max-width: 100%; border-radius: 4px; }
.section-card { position: relative; padding-bottom: 12px; margin-top: 28px; }
.section-card .q-foot { border-top: 0; margin-top: 4px; padding-top: 0; }
.section-head {
  position: absolute;
  top: 0;
  left: 0;
  transform: translateY(-100%);
  padding: 6px 12px;
  background: #5746e3;
  border-radius: 8px 8px 0 0;
}
.section-head p { margin: 0; color: #fff; font-size: 13px; }
.section-menu { position: relative; }
.section-menu > button {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #5f6368;
  cursor: pointer;
}
.section-menu > button:hover { background: #f1f3f4; }
.section-menu-list {
  position: absolute;
  right: 0;
  top: 36px;
  z-index: 5;
  min-width: 180px;
  padding: 8px 0;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.section-menu-list.up { top: auto; bottom: 36px; }
.section-menu-list button {
  display: block;
  width: 100%;
  padding: 8px 16px;
  border: 0;
  background: transparent;
  color: #202124;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}
.section-menu-list button:hover { background: #f1f3f4; }
.section-menu-list button:disabled { color: #bdbdbd; cursor: default; background: transparent; }
.section-kicker { margin: 0 0 8px; color: #5f6368; }
.section-title { font-size: 24px; padding: 8px 0; }
.gf-tools {
  position: absolute;
  top: 0;
  left: calc(100% + 12px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.24);
  overflow: hidden;
}
.gf-row:has(.section-card) .gf-tools { top: 28px; }
.gf-tools button { width: 48px; height: 44px; border: 0; background: #fff; color: #5f6368; font-size: 20px; cursor: pointer; }
.settings-card { display: flex; flex-direction: column; gap: 12px; }
.settings-card h2 { margin: 0; font-size: 18px; font-weight: 500; }
.settings-card label, .button-row { display: flex; flex-direction: column; gap: 4px; color: #5f6368; font-size: 13px; }
.button-row { flex-direction: row; align-items: center; flex-wrap: wrap; }
.button-row input, .button-row select { flex: 1; min-width: 120px; }
.button-row input.invalid { border-color: #d93025; }
.check { flex-direction: row; align-items: center; gap: 4px; white-space: nowrap; }
.settings-card .setting-switch { flex-direction: row; align-items: center; justify-content: space-between; gap: 12px; color: #202124; font-size: 14px; }
.setting-name { display: flex; align-items: center; gap: 6px; min-width: 0; }
.settings-card .setting-name label { flex-direction: row; gap: 0; color: inherit; font-size: inherit; cursor: pointer; }
.help-btn {
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  border: 1px solid #dadce0;
  border-radius: 50%;
  background: #fff;
  color: #5f6368;
  font-size: 12px;
  line-height: 16px;
  padding: 0;
  cursor: pointer;
}
.help-btn[aria-expanded="true"] { border-color: #5746e3; color: #5746e3; background: #f3f2fd; }
.help-pop {
  margin: 8px 0 0;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 8px;
  color: #5f6368;
  font-size: 13px;
  line-height: 1.5;
}
.setting-switch.off .setting-name label,
.setting-switch.off .switch { opacity: 0.55; }
.setting-switch .switch { position: relative; width: 36px; height: 20px; flex: 0 0 36px; }
.setting-switch .switch input {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 36px;
  height: 20px;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  appearance: none;
  opacity: 0;
  cursor: pointer;
}
.setting-switch .switch input:disabled { cursor: not-allowed; }
.setting-switch .switch i {
  display: block;
  width: 36px;
  height: 14px;
  margin: 3px 0 0;
  background: #bdbdbd;
  border-radius: 8px;
  position: relative;
  pointer-events: none;
}
.setting-switch .switch i::after {
  content: '';
  position: absolute;
  top: -3px;
  left: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fafafa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}
.setting-switch .switch input:checked + i { background: #c5bff6; }
.setting-switch .switch input:checked + i::after { left: 16px; background: #5746e3; }
.hint, .fill-path { margin: 0; color: #5f6368; }
.fill-path { word-break: break-all; }
.gf-error { width: min(820px, 100%); margin: 0 auto 12px; color: #b91c1c; }
.intake-select { margin-top: 8px; }
@media (max-width: 900px) {
  .gf-canvas { padding-left: 16px; padding-right: 16px; }
  .gf-stack, .gf-error { width: 100%; }
  .gf-tools { position: static; flex-direction: row; width: fit-content; margin-top: 8px; }
  .q-head { flex-direction: column; }
}
</style>
